'use client';

/**
 * GRS search service
 * Each search service needs a URL constructor and a response parser.
 */
import { getNumberOfPages, isBDSite } from '../utils';
import type { ReadonlyURLSearchParams } from 'next/navigation';

import { GRS_REFINE_QUERY_PARAM } from '@/components/SearchResultsFacets/constants';
import {
    GRS_BOPIM_FACET_KEY,
    GRS_BUY_IN_WAREHOUSE_FACET_KEY,
    GRS_CATEGORY_URI_FACET_KEY,
    GRS_HIDE_OUT_OF_STOCK_FACET_KEY,
    GRS_SHOW_OUT_OF_STOCK_FACET_KEY,
    GRS_WAREHOUSE_PICKUP_FACET_KEY,
    grsBooleanValueFacets,
    grsRangeValueFacets,
} from '@/components/SearchResultsFacets/grs.constants';
import { isGRSPriceFacetKey } from '@/components/SearchResultsFacets/grs.lib';
import {
    fullyDecodeURI,
    splitRefinementPairs,
} from '@/components/SearchResultsFacets/utils';
import {
    GRS_REFINE_FILTER_KEY_VALUE_DELIMITER,
    PageTypes,
    REFINE_QUERY_DELIMITER,
} from '@/constants/index';
import { getKeyValue } from '@/services/content/data';
import { GRS_PRICE_FACET_WAREHOUSE_KEY } from '@/services/search/services/grs.constants';
import type {
    GrsConfig,
    SearchPageConfigProperties,
} from '@/src/types/contentStack';
import type {
    GRSFacetConfig,
    GRSFacetConfigProperties,
} from '@/types/contentStack';
import { GRSBreadcrumb } from '@/types/searchQuery';
import { splitByCommaOutsideParentheses } from '@/utils/searchFacets';
import type {
    ContentStackEntryDataProps,
    LocaleProps,
} from '@nxt-costco-com/forge-digital-components';

import { SearchResultGRS, WarehouseSearchParams } from './GRS.types';
import type {
    AdTargetingFlags,
    GRSSearchResultWithInventory,
} from './GRS.types';

const grsKeyQueryMap: Record<string, (locationId: string) => string> = {
    [GRS_SHOW_OUT_OF_STOCK_FACET_KEY]: () => 'SHOW_OUT_OF_STOCK',
    [GRS_HIDE_OUT_OF_STOCK_FACET_KEY]: () => 'HIDE_OUT_OF_STOCK',
    [GRS_BOPIM_FACET_KEY]: mdoLocationId =>
        `(attributes.program_types: ANY(\"LTLPickUp\") AND (inventory(${mdoLocationId}, attributes.availability): ANY(\"IN_STOCK\", \"LOW_STOCK\", \"BACK_ORDER\", \"PRESELL\", \"SHIP_RESTRICTED\") OR (attributes.disp_on_zero_inv = 1 AND inventory(${mdoLocationId}, attributes.availability): ANY(\"OUT_OF_STOCK\")) ))`,
    [GRS_BUY_IN_WAREHOUSE_FACET_KEY]: warehouseId =>
        `(attributes.program_types: ANY(\"InWarehouse\") AND (inventory(${warehouseId}, attributes.availability): ANY(\"IN_STOCK\", \"LOW_STOCK\", \"BACK_ORDER\", \"PRESELL\", \"SHIP_RESTRICTED\") OR (attributes.disp_on_zero_inv = 1 AND inventory(${warehouseId}, attributes.availability): ANY(\"OUT_OF_STOCK\")) ))`,
    [GRS_WAREHOUSE_PICKUP_FACET_KEY]: warehouseId =>
        `((attributes.program_types: ANY("UseWarehouseInventory") AND (inventory(${warehouseId}, attributes.availability): ANY("IN_STOCK", "LOW_STOCK", "BACK_ORDER", "PRESELL", "SHIP_RESTRICTED") OR (attributes.disp_on_zero_inv = 1 AND inventory(${warehouseId}, attributes.availability): ANY("OUT_OF_STOCK")))) OR attributes.delivery_type: ANY(\"Warehouse Pick-up\") OR attributes.delivery_type: ANY(\"Cueillette à l’entrepôt\"))`,
    ['default']: () => '',
};

let cachedQueryParamKeyToFacetKeyMap: Record<string, string> | null = null;

export const createQueryParamKeyToFacetKeyMap = (
    grsFacetConfig: GRSFacetConfig,
): Record<string, string> => {
    if (cachedQueryParamKeyToFacetKeyMap) {
        return cachedQueryParamKeyToFacetKeyMap;
    }

    const queryParamKeyToFacetKeyMap: Record<string, string> = {};
    for (const [key, value] of Object.entries(grsFacetConfig)) {
        queryParamKeyToFacetKeyMap[value.queryParamKey ?? key] = key;
    }

    cachedQueryParamKeyToFacetKeyMap = queryParamKeyToFacetKeyMap;
    return queryParamKeyToFacetKeyMap;
};

export const resetQueryParamKeyToFacetKeyMapCache = () => {
    cachedQueryParamKeyToFacetKeyMap = null;
};

export const getFacetKeyFromQueryParamKey = (
    queryParamKey: string,
    grsFacets: GRSFacetConfig,
) => {
    const queryParamKeyToFacetKeyMap =
        createQueryParamKeyToFacetKeyMap(grsFacets);
    const facetKey = queryParamKeyToFacetKeyMap[queryParamKey];
    return facetKey;
};

export const getGRSFacetsFromConfig = (
    grsFacetConfig: ContentStackEntryDataProps<GRSFacetConfigProperties>,
): GRSFacetConfig => {
    const grsAttributesFacets = grsFacetConfig?.properties?.[0]?.attributes;
    const updatedAttributesFacets: Record<string, unknown> = {};

    if (grsAttributesFacets) {
        // Update the keys to include the 'attributes.' prefix so they align with the GRS API
        // Contentstack would not allow a period in the key name so we have to add it here
        for (const [key, value] of Object.entries(grsAttributesFacets)) {
            const updatedKey = `attributes.${key}`;
            updatedAttributesFacets[updatedKey] = value;
        }
    }

    return {
        ...grsFacetConfig?.properties?.[0]?.standard,
        ...updatedAttributesFacets,
    } as GRSFacetConfig;
};

const getOrderBy = (searchParams: ReadonlyURLSearchParams) => {
    const sortBy = searchParams.get('sortBy');

    switch (sortBy) {
        case 'score+desc': {
            return null;
        }
        case 'item_location_pricing_salePrice+desc': {
            return 'price desc';
        }
        case 'item_location_pricing_salePrice+asc': {
            return 'price';
        }
        case 'item_review_ratings+desc': // CABC
        case 'item_ratings+desc': {
            return 'rating desc';
        }
        case 'item_startDate+desc': {
            return 'attributes.start_date_epoch desc';
        }
        case 'item_page_views+desc': {
            return null;
        }
        case 'Brand_attr+asc': {
            return 'attributes.brand';
        }
        case 'item_orders+desc': {
            // Best sellers sort
            return 'attributes.order_count';
        }
        default: {
            return null;
        }
    }
};

export const getGRSQueryRangeValue = (
    key: string,
    values: string[],
    skipKeyInsert = false,
) => {
    // Convert the string tuples into number pairs
    const parsedRanges = values
        .map(value => {
            const trimmedValue = value.trim();
            const match = trimmedValue.match(/\((\d+),(\d+)\)/);
            if (match && match[1] !== undefined && match[2] !== undefined) {
                return [parseInt(match[1], 10), parseInt(match[2], 10)];
            }
            return null;
        })

        .filter((item): item is [number, number] => item !== null) as [
        number,
        number,
    ][];

    // Sort ranges by the start value
    parsedRanges.sort((a, b) => a[0] - b[0]);

    const consolidated: [number, number][] = [];

    for (const [start, end] of parsedRanges) {
        const lastRange = consolidated.at(-1);
        if (lastRange && lastRange[1] >= start) {
            // Merge overlapping or adjacent ranges
            lastRange[1] = Math.max(lastRange[1], end);
        } else {
            consolidated.push([start, end]);
        }
    }

    // Format the output string
    return consolidated
        .map(([s, e]) =>
            skipKeyInsert ? `${s}i, ${e}i` : `${key}: IN(${s}i, ${e}i)`,
        )
        .join(' OR ');
};

/** Builds the GRS price facet filter key, injecting the province code for CA and/or BD warehouse ID if necessary.
 * e.g:
 * USBC   -> inventory(847, price)
 * CABC   -> inventory(894_ON, price)
 * CABD   -> inventory(1668_ON, price)  (bdWarehouseId="1668", provinceCode="ON")
 * USBD   -> inventory(115, price)  (bdWarehouseId="115")
 */
const getGRSPriceFacetPayloadKey = (
    facetKey: string,
    provinceCode: string | undefined,
    bdWarehouseId?: string,
) => {
    // Only modify price facet keys (any inventory(XXXX, price) format)
    if (!isGRSPriceFacetKey(facetKey)) return facetKey;

    // For BD sites, override the warehouse ID with the actual BD location
    const warehouseId =
        bdWarehouseId ?? facetKey.match(/^inventory\((\d{1,4}), price\)$/)?.[1];

    const normalizedProvinceCode = provinceCode?.trim().toUpperCase();

    // Only append province code for Canadian provinces
    const isCanada =
        process.env.NEXT_PUBLIC_SITE?.toUpperCase().startsWith('CA');

    const shouldAppendProvince = !!normalizedProvinceCode && isCanada;

    return warehouseId
        ? `inventory(${warehouseId}${shouldAppendProvince ? '_' + normalizedProvinceCode : ''}, price)`
        : facetKey;
};

export const getGRSFilterBy = ({
    searchParams,
    whloc,
    grsFacetConfig,
    categoryPageId,
    mdoLocationId = '',
    isWAPOEnabledWarehouse = false,
    userLocation,
    bdWarehouseId,
    site = process.env.SITE! || process.env.NEXT_PUBLIC_SITE!,
}: {
    searchParams: ReadonlyURLSearchParams | undefined;
    whloc: string | undefined;
    grsFacetConfig:
        | ContentStackEntryDataProps<GRSFacetConfigProperties>
        | undefined;
    categoryPageId: string | undefined;
    mdoLocationId: string | undefined;
    isWAPOEnabledWarehouse?: boolean;
    facetConfig?: ContentStackEntryDataProps<unknown> | undefined;
    userLocation?: string | undefined;
    /** For BD sites: the warehouse location ID (from `loc`) used to build the price filter key */
    bdWarehouseId?: string | undefined;
    site?: string;
}) => {
    if (!searchParams || !grsFacetConfig) return [];

    const grsFilterParams = new URLSearchParams();
    const grsFacets = getGRSFacetsFromConfig(grsFacetConfig);
    const grsFilterParamKeys = Object.keys(grsFacets);

    const refinementArray =
        fullyDecodeURI(searchParams.get(GRS_REFINE_QUERY_PARAM))?.split(
            REFINE_QUERY_DELIMITER,
        ) || [];

    refinementArray.forEach(value => {
        const [filterParamKey, filterValue] = splitRefinementPairs(
            value,
            GRS_REFINE_FILTER_KEY_VALUE_DELIMITER,
        );
        const facetKey = getFacetKeyFromQueryParamKey(
            filterParamKey || '',
            grsFacets,
        );
        if (facetKey && grsFilterParamKeys.includes(facetKey)) {
            // Use the resolved facet key when it is valid and configured
            grsFilterParams.append(facetKey, filterValue || '');
        } else if (filterParamKey) {
            // Only append raw filterParamKey when it is truthy to avoid empty keys
            grsFilterParams.append(filterParamKey, filterValue || '');
        }
    });

    if (categoryPageId) {
        grsFilterParams.append(GRS_CATEGORY_URI_FACET_KEY, categoryPageId);
    }

    const getGRSQueryDynamicValue = (key: string, values: string[]) => {
        const formattedValues = values.map(value => `\"${value}\"`).join(', ');
        return `${key}: ANY(${formattedValues})`;
    };

    const getGRSQueryValue = (
        key: string,
        values: string[],
        skipKeyInsert = false,
    ) => {
        const payloadFacetKey = getGRSPriceFacetPayloadKey(
            key,
            userLocation,
            bdWarehouseId,
        );
        // Price facets use range queries; check both the static list and the dynamic pattern
        if (grsRangeValueFacets.includes(key) || isGRSPriceFacetKey(key)) {
            return getGRSQueryRangeValue(
                payloadFacetKey,
                values,
                skipKeyInsert,
            );
        } else {
            return getGRSQueryDynamicValue(payloadFacetKey, values);
        }
    };

    const grsFilterParamsEntries = Array.from(grsFilterParams.entries());

    const grsProgramTypeFacetKeys = [
        GRS_BOPIM_FACET_KEY,
        GRS_BUY_IN_WAREHOUSE_FACET_KEY,
        GRS_WAREHOUSE_PICKUP_FACET_KEY,
    ];

    const filterBy: string[] = [];

    // Program Type Filters separated so they can be processed as single filter with OR condition
    const programTypeFilters = [];
    let showOutOfStockEnabled = false;

    for (const [key, value] of grsFilterParamsEntries) {
        const values = splitByCommaOutsideParentheses(value);
        const filterValues: string[] = [];

        if (grsBooleanValueFacets.includes(key)) {
            const isTrue = values[0] === 'true';
            if (key === GRS_SHOW_OUT_OF_STOCK_FACET_KEY) {
                showOutOfStockEnabled = isTrue;
                continue;
            }

            if (isTrue && (whloc || mdoLocationId) && grsKeyQueryMap[key]) {
                const locationId =
                    key === GRS_BOPIM_FACET_KEY
                        ? mdoLocationId
                        : (whloc as string);
                const queryFn =
                    grsKeyQueryMap[key] ?? grsKeyQueryMap['default'];
                if (!queryFn) continue;
                const query = queryFn(locationId);
                if (query) {
                    if (grsProgramTypeFacetKeys.includes(key)) {
                        programTypeFilters.push(query);
                    } else {
                        filterValues.push(query);
                    }
                }
            }
        } else if (values.length > 0) {
            if (isGRSPriceFacetKey(key) && isWAPOEnabledWarehouse) {
                filterValues.push(getGRSQueryValue(key, values, true));
            } else {
                filterValues.push(getGRSQueryValue(key, values, false));
            }
        }

        if (filterValues.length) {
            filterBy.push(filterValues.join(' OR '));
        }
    }

    // The default "HIDE_OUT_OF_STOCK" filter only applies to BC sites,
    // BD sites do not have default stock facet params
    if (!showOutOfStockEnabled && !isBDSite(site)) {
        const hideOutOfStockQuery =
            grsKeyQueryMap[GRS_HIDE_OUT_OF_STOCK_FACET_KEY]?.('');
        if (hideOutOfStockQuery) {
            filterBy.push(hideOutOfStockQuery);
        }
    }

    if (programTypeFilters.length) {
        filterBy.push(programTypeFilters.join(' OR '));
    }

    return filterBy;
};

const generateRequestBody = (
    grsConfig: GrsConfig,
    grsFacetConfig: ContentStackEntryDataProps<GRSFacetConfigProperties>,
    searchParams: ReadonlyURLSearchParams,
    currentPage: number,
    adobeSessionId: string,
    { loc, mdo, userLocation, userZipCode, whloc }: WarehouseSearchParams,
    categoryPageId: string | undefined,
    resultsPerPage: number,
    userInfoId: string | undefined,
    isWAPOEnabledWarehouse: boolean = false,
    facetConfig: ContentStackEntryDataProps<unknown> | undefined,
    site: string,
) => {
    let body = grsConfig?.required_request_parameters;
    const keyword = searchParams.get('keyword');

    const filterBy = getGRSFilterBy({
        searchParams,
        whloc,
        grsFacetConfig,
        categoryPageId,
        mdoLocationId: mdo,
        isWAPOEnabledWarehouse,
        facetConfig,
        userLocation,
        // For BD sites, use the warehouse location to build the correct price filter key
        bdWarehouseId: isBDSite(site) ? loc : undefined,
        site,
    });
    const priceFacetWarehouse = getKeyValue(
        GRS_PRICE_FACET_WAREHOUSE_KEY,
        grsFacetConfig,
    );

    const orderBy = getOrderBy(searchParams);

    const pageCategories = categoryPageId ? [categoryPageId] : [];

    const locations = loc?.split(',') || [];
    // "*" used in LW but causes error in GRS query
    const filteredLocations = locations.filter(loc => loc !== '*');

    let deliveryLocations =
        priceFacetWarehouse && !isBDSite(site)
            ? [priceFacetWarehouse, ...filteredLocations]
            : filteredLocations;

    const offset = resultsPerPage * (currentPage - 1);
    const bdWh = isBDSite(site) ? loc : undefined;

    body = {
        ...body,
        deliveryLocations,
        filterBy: filterBy ?? [],
        // TODO - Remove conditional addition of mdoLocationId property once API is updated to allow null values without causing error
        ...(mdo ? { mdoLocationId: mdo } : {}),
        pageSize: resultsPerPage,
        offset: offset,
        orderBy: orderBy,
        pageCategories,
        personalizationEnabled: body.personalizationEnabled,
        query: keyword ?? '',
        searchMode: 'page',
        shipToPostal: userZipCode,
        shipToState: userLocation,
        visitorId: adobeSessionId,
        ...(userInfoId ? { userInfo: { userId: userInfoId } } : {}),
        warehouseId: whloc || bdWh,
    };
    return { body: JSON.stringify(body), filterBy };
};

export function getAdTargetingFlags({
    data,
    searchFsaExemptKeywords,
    categoryFsaFlag,
    keyword,
    searchParams,
    includeEmptyPagination = false,
}: {
    data?: SearchResultGRS;
    searchFsaExemptKeywords?: string[];
    categoryFsaFlag: boolean;
    keyword?: string | null;
    searchParams?: ReadonlyURLSearchParams;
    includeEmptyPagination?: boolean;
}): AdTargetingFlags {
    const adTargetingFlags: AdTargetingFlags = {} as AdTargetingFlags;

    const searchFacets = data?.searchResult?.facets;
    const doesFsaFacetExist =
        (
            searchFacets?.find(facet => facet.key === 'attributes.fsa_eligible')
                ?.values || []
        ).length > 0;

    const doesChdiFacetExist = !!searchFacets?.find(
        facet => facet.key === 'attributes.chdi_eligible',
    )?.values?.length;

    adTargetingFlags.isAdTargetingExplicitlyDisabled =
        doesFsaFacetExist || doesChdiFacetExist;
    const finalKeyword = keyword || searchParams?.get('keyword');
    adTargetingFlags.isAdTargetingExceptionEnabled = Boolean(
        searchFsaExemptKeywords?.some(
            csKeyword =>
                csKeyword?.toLowerCase() === finalKeyword?.toLowerCase(),
        ) || categoryFsaFlag,
    );
    adTargetingFlags.isAdTargetingEnabled =
        adTargetingFlags.isAdTargetingExceptionEnabled ||
        !adTargetingFlags.isAdTargetingExplicitlyDisabled;

    if (includeEmptyPagination) {
        adTargetingFlags.pagination = {
            currentPage: 0,
            totalDocs: 0,
            totalPages: 0,
        };
    }
    return adTargetingFlags;
}

/**
 * GRS Search Service.
 */

export async function GrsQuery(
    grsConfig: GrsConfig | undefined,
    grsFacetConfig:
        | ContentStackEntryDataProps<GRSFacetConfigProperties>
        | undefined,
    searchParams: ReadonlyURLSearchParams,
    lang: LocaleProps,
    properties: SearchPageConfigProperties | undefined,
    { loc, mdo, whloc, userLocation, userZipCode }: WarehouseSearchParams,
    pageType: string,
    site: string,
    adobeSessionId: string,
    categoryPageId: string | undefined,
    searchFsaExemptKeywords: string[] | undefined,
    categoryFsaFlag: boolean,
    userInfoId: string | undefined,
    isWAPOEnabledWarehouse: boolean = false,
    facetConfig: ContentStackEntryDataProps<unknown> | undefined,
): Promise<SearchResultGRS> {
    //return value should be updated later depending on how we want the response
    let returnValue: SearchResultGRS = {} as SearchResultGRS;
    const { resultsPerPage } = properties || { resultsPerPage: 24 };

    if (!grsConfig || !grsFacetConfig || !facetConfig) {
        return returnValue;
    }

    const keyword = searchParams.get('keyword');
    if (!keyword && pageType === PageTypes.SEARCH) {
        return returnValue;
    }

    const currentPage = Number(searchParams.get('currentPage')) || 1;

    const formattedLocale = lang
        .split('-')
        .map((part: string, index: number) => {
            if (index === 1) {
                return part.toUpperCase(); // Only capitalize the second part
            }
            return part;
        })
        .join('-');
    const requestHeaders = {
        ...grsConfig.required_request_headers,
        locale: formattedLocale,
        client_id: site,
    };
    const { body, filterBy } = generateRequestBody(
        grsConfig,
        grsFacetConfig,
        searchParams,
        currentPage,
        adobeSessionId,
        {
            loc,
            mdo,
            userLocation,
            userZipCode,
            whloc,
        },
        categoryPageId,
        resultsPerPage,
        userInfoId,
        isWAPOEnabledWarehouse,
        facetConfig,
        site,
    );

    const response = await fetch(grsConfig?.endpoint, {
        method: 'POST',
        headers: {
            ...requestHeaders,
        },
        body: body,
    });

    if (!response.ok) {
        console.error('GRS ERROR', response.statusText);
        return returnValue;
    }
    const data: SearchResultGRS = await response.json();

    const pagination = {
        currentPage: currentPage,
        totalDocs: data?.searchResult?.totalSize,
        totalPages: getNumberOfPages(
            data?.searchResult?.totalSize || 0,
            resultsPerPage,
        ),
    };

    const resultsMappedWithInventory = data?.searchResult?.results?.map(
        (result: GRSSearchResultWithInventory) => {
            const matchingInventory = data.inventoryResponse?.find(
                inventory => inventory.productId === result.id,
            );
            return {
                ...result,
                inventory: matchingInventory!,
                mdoLocation: data.mdoLocation,
            };
        },
    );

    const adTargetingFlags = getAdTargetingFlags({
        data,
        searchFsaExemptKeywords,
        categoryFsaFlag,
        keyword,
    });
    returnValue = {
        ...data,
        // Preserve the actual request, not facets reconstructed from the response.
        requestFilters: filterBy,
        pagination,
        searchResult: {
            ...data?.searchResult,
            results: resultsMappedWithInventory,
        },
        metrics: {
            semantic: false,
        },
        ...adTargetingFlags,
    } as SearchResultGRS;

    // adding new redirect property to the return value so that we can follow LucidWorks like structure
    returnValue.redirect = data?.searchResult?.redirectUri ?? '';
    returnValue.metrics.correction = data.searchResult?.correctedQuery;
    returnValue.metrics.original =
        (data.searchResult?.correctedQuery && keyword) || '';
    returnValue.metrics.semantic = false;
    // !!data.searchResult?.queryExpansionInfo?.expandedQuery;
    // parseBreadCrumbs returns an array of breadcrumb objects; cast to the expected GRSBreadcrumb
    // to satisfy the SearchResultGRS.breadcrumb type so TypeScript compilation succeeds.
    returnValue.breadcrumb = parseBreadCrumbs(
        returnValue.breadcrumb as GRSBreadcrumb,
    ) as unknown as GRSBreadcrumb;

    return returnValue;
}

export const parseBreadCrumbs = (breadCrumbs: GRSBreadcrumb | undefined) => {
    const categoryUrls = breadCrumbs?.url?.split('|') ?? [];
    const breadCrumbsObj = breadCrumbs?.name
        ?.split('|')
        .map((categoryName: string, index: number) => ({
            name: categoryName,
            url:
                categoryUrls.length > index
                    ? categoryUrls[index] && `${categoryUrls[index]}.html`
                    : '',
        }));
    return breadCrumbsObj;
};

export const isSearchResultGRS = (
    result: unknown,
): result is SearchResultGRS => {
    if (!result || typeof result !== 'object') return false;
    const r = result as Record<string, unknown>;
    return (
        'searchResult' in r &&
        r.searchResult !== null &&
        typeof r.searchResult === 'object' &&
        'results' in (r.searchResult as object) &&
        Array.isArray((r.searchResult as Record<string, unknown>).results)
    );
};

export const getSearchResultsType = (searchResult: SearchResultGRS) => {
    const searchResultData = searchResult?.searchResult;

    if (!searchResultData) return null;

    const { correctedQuery, queryExpansionInfo, redirectUri, totalSize } =
        searchResultData;

    if (correctedQuery) return 'corrected';
    if (queryExpansionInfo?.expandedQuery) return 'loosened';
    if (totalSize === 0) return 'zero';
    if (redirectUri) return 'redirect';

    return 'expected';
};
